import React, { useCallback } from 'react';

import { ActionIcon, Group, Paper, Title, Tooltip, createStyles, Text } from '@mantine/core';
import { DatePickerInput, DatesRangeValue } from '@mantine/dates';
import { IconFilterOff } from '@tabler/icons-react';
import BackButton from 'components/BackButton';
import DownloadCSV from 'components/DownloadCSV';
import HelpButton from 'components/HelpButton';
import dayjs from 'dayjs';
import usePackageRevenue from 'hooks/reports/revenue/usePackageRevenue';
import { useNavigate } from 'react-router-dom';
import { PackageRevenueReport } from 'types';
import { setDateDifference } from 'utils/date-functions';

import { SearchFilters } from '../types';

const useStyles = createStyles(theme => ({
  header: {
    width: '100%',
    background: theme.colors.gray[0],
    padding: theme.spacing.xl,
    paddingBottom: theme.spacing.xl,
    display: 'grid',
    borderBottom: `1px solid ${theme.colors.gray[3]}`,
    borderRadius: 0,
    gridTemplateColumns: 'min-content auto max-content min-content min-content',
    gridTemplateRows: 'auto',
    gridTemplateAreas: `"backButton title dateRange clear download"`,
    alignItems: 'center',
    gap: theme.spacing.sm,
    [theme.fn.smallerThan('md')]: {
      gridTemplateColumns: 'min-content auto min-content min-content',
      gridTemplateAreas: `
        'backButton title clear download'
        'dateRange dateRange dateRange dateRange'
        `,
    },
  },
  backButton: {
    gridArea: 'backButton',
  },
  title: {
    gridArea: 'title',
  },
  dateRange: {
    gridArea: 'dateRange',
  },
  clear: {
    gridArea: 'clear',
  },
  download: {
    gridArea: 'download',
  },
}));

const csvOptions = {
  fieldSeparator: ',',
  quoteStrings: '',
  decimalSeparator: '.',
  showLabels: true,
  useBom: true,
  useKeysAsHeaders: true,
};

const dataToRow = (d: PackageRevenueReport): { [key: string]: any } => ({
  Package: d.package,
  Athletes: d.athletes,
  Packages: d.athletePackages,
  Invoices: d.invoices,
  Revenue: (d.revenue / 100).toFixed(2),
});

const PackageRevenuePageHeader = ({
  initialSearchFiltersValue,
  searchFilters,
  setSearchFilters,
  orderingValue,
}: {
  initialSearchFiltersValue: SearchFilters;
  searchFilters: SearchFilters;
  setSearchFilters: React.Dispatch<React.SetStateAction<SearchFilters>>;
  orderingValue: string;
}): JSX.Element => {
  const { classes } = useStyles();
  const navigate = useNavigate();

  const handleBackClick = useCallback((): void => {
    navigate(-1);
  }, [navigate]);

  const handleDateChange = (value: DatesRangeValue): void => {
    let adjustedDate: DatesRangeValue = [...value];
    if (!!value[1]) {
      const adjustedEndDate = value[1];
      adjustedDate[1] = setDateDifference(adjustedEndDate, 0, 0, 0, 23, 59, 59);
    }
    setSearchFilters(prev => ({ ...prev, dateRange: adjustedDate }));
  };

  const handleClearFilters = (): void => {
    setSearchFilters(initialSearchFiltersValue);
  };

  return (
    <Paper className={classes.header}>
      <BackButton handleOnClick={handleBackClick} />
      <Group noWrap spacing='xl' className={classes.title} data-cy='PackageRevenuePageHeader'>
        <Title order={1}>Package Revenue</Title>
        <HelpButton width={400} position='right'>
          <Text size='sm'>{'Revenue broken down by package within the selected date range.'}</Text>
        </HelpButton>
      </Group>
      <DatePickerInput
        className={classes.dateRange}
        value={searchFilters.dateRange}
        onChange={handleDateChange}
        valueFormat='MMMM DD, YYYY'
        type='range'
        allowSingleDateInRange
        maxDate={new Date()}
        required
        {...{ placeholder: 'Select date range' }}
      />
      <Tooltip label='Clear filters' className={classes.clear}>
        <ActionIcon onClick={handleClearFilters}>
          <IconFilterOff />
        </ActionIcon>
      </Tooltip>

      <DownloadCSV
        useList={usePackageRevenue}
        dataToRow={dataToRow}
        csvOptions={csvOptions}
        hookOptions={{
          dateStart: searchFilters.dateRange[0] ? searchFilters.dateRange[0].toISOString() : undefined,
          dateEnd: searchFilters.dateRange[1] ? searchFilters.dateRange[1].toISOString() : undefined,
          ordering: orderingValue,
        }}
        limit='100'
        className={classes.download}
        reportType='package-revenue'
        startDate={
          searchFilters.dateRange[0] ? dayjs(searchFilters.dateRange[0]).format('YYYY-MM-DD') : null
        }
        endDate={searchFilters.dateRange[1] ? dayjs(searchFilters.dateRange[1]).format('YYYY-MM-DD') : null}
      />
    </Paper>
  );
};

export default PackageRevenuePageHeader;
